/**
 * AI Execution Status Types
 *
 * Defines the normalized step/status model for tracking and displaying
 * AI execution progress in the chat UI.
 *
 * These types provide a clean abstraction layer between backend streaming events
 * and frontend visualization, enabling:
 * - High-level execution status summary
 * - Detailed step-by-step progress tracking
 * - User-friendly presentation labels
 * - Debug metadata for developers
 *
 * Design principles:
 * - Separate presentation labels from internal event names
 * - Support incremental streaming updates
 * - Handle out-of-order events gracefully
 * - Extensible for future workflow types
 *
 * See context/50-ai/06-ai-chat.md for integration details.
 */
/**
 * Labels for the generation (`step-generate`) step. The compute window before
 * the first visible token reads as "Thinking"; once tokens stream it flips to
 * "Responding". Shared so the backend (which starts the step) and the frontend
 * (which flips the label on the first `message_delta`) can never drift.
 */
export const GENERATION_THINKING_LABEL = 'Thinking';
export const GENERATION_RESPONDING_LABEL = 'Responding';
/**
 * Human-readable label for each execution phase.
 */
export function ExecutionPhaseLabel(phase) {
    switch (phase) {
        case 'queued':
            return 'Queued';
        case 'initializing':
            return 'Preparing';
        case 'retrieving_context':
            return 'Retrieving context';
        case 'planning':
            return 'Planning';
        case 'reasoning':
            return 'Thinking';
        case 'executing_tools':
            return 'Working';
        case 'generating_response':
            return 'Responding';
        case 'completed':
            return 'Completed';
        case 'failed':
            return 'Failed';
        case 'cancelled':
            return 'Cancelled';
    }
}
/**
 * Canonical, ordered definition of the deal-generation plan steps - the single
 * source of truth for their ids, titles and descriptions. Consumed by the AI
 * runtime's plan template (`DEAL_GENERATION_TEMPLATE` / `DEAL_PLAN_STEP_IDS` in
 * plan-generator.ts) and by the `'plan-*'` entries of {@link STEP_DESCRIPTORS}
 * below, so the labels can never drift between the two.
 */
export const DEAL_PLAN_STEPS = [
    {
        id: 'plan-estimate',
        type: 'analysis',
        title: 'Generate cost estimate',
        description: 'Compute estimate from questionnaire answers and save it.',
    },
    {
        id: 'plan-rationale',
        type: 'analysis',
        title: 'Enrich estimate notes',
        description: 'Generate concise client-facing notes for each estimate line item.',
        dependsOn: ['plan-estimate'],
    },
    {
        id: 'plan-resource-plan',
        type: 'analysis',
        title: 'Generate resource plan',
        description: 'Produce a role-based resource plan from the saved estimate.',
        dependsOn: ['plan-rationale'],
    },
    {
        id: 'plan-sow',
        type: 'response',
        title: 'Generate statement of work',
        description: 'Draft the SOW from questionnaire responses and resource plan.',
        dependsOn: ['plan-resource-plan'],
    },
];
/**
 * Single source of truth for the orchestrator's stable pipeline/plan stepIds.
 *
 * Keyed on the exact stepIds emitted in orchestrator.ts. The five mechanical
 * pipeline stages are `userFacing: false` (meaningless to end users); only the
 * history-load step is user-facing, and even that is hidden in scoped mode.
 *
 * Unknown stepIds (skill- and tool-emitted, dynamic) are NOT listed here and
 * fail open (treated as user-facing) - see {@link isUserFacingStep}.
 */
export const STEP_DESCRIPTORS = {
    'step-classify-intent': { kind: 'system', defaultLabel: 'Classifying intent', userFacing: false },
    // History inclusion is decided per-intent (classifiedIntent.requiresHistory)
    // and is mechanical bookkeeping - not shown to end users (still traced).
    'step-load-history': {
        kind: 'system',
        defaultLabel: 'Loaded recent messages',
        userFacing: false,
    },
    'step-build-context': { kind: 'system', defaultLabel: 'Building context', userFacing: false },
    'step-build-profile': {
        kind: 'system',
        defaultLabel: 'Adapting response profile',
        userFacing: false,
    },
    'step-authorize': { kind: 'system', defaultLabel: 'Evaluating action policy', userFacing: false },
    'step-context-policy': {
        kind: 'system',
        defaultLabel: 'Evaluating context policy',
        userFacing: false,
    },
    // Deal-generation plan steps derive their labels from the canonical
    // DEAL_PLAN_STEPS above, so they cannot drift from the runtime plan template.
    ...Object.fromEntries(DEAL_PLAN_STEPS.map((s) => [
        s.id,
        { kind: 'plan', defaultLabel: s.title, userFacing: true },
    ])),
};
/** Look up the descriptor for a stable stepId, if any. */
export function getStepDescriptor(stepId) {
    return STEP_DESCRIPTORS[stepId];
}
/**
 * Decide whether a step should be shown to the end user.
 *
 * - Unknown stepIds fail open (user-facing) so skill/tool steps are never lost.
 * - In `scoped` mode, all `pipeline`/`system` steps are hidden (single-purpose
 *   button actions don't need history/context/policy chatter).
 * - Otherwise the descriptor's own `userFacing` flag applies (the mechanical
 *   pipeline stages stay hidden even in full mode).
 */
export function isUserFacingStep(stepId, scoped = false) {
    const desc = STEP_DESCRIPTORS[stepId];
    if (!desc)
        return true;
    if (scoped && (desc.kind === 'pipeline' || desc.kind === 'system'))
        return false;
    return desc.userFacing;
}
/**
 * Resolve a step label. A caller-supplied dynamic `override` (e.g. "Retrieved 4
 * recent messages") wins; otherwise the registry's canonical label is used;
 * unknown stepIds fall back to a Title-Cased form of the id.
 */
export function getStepLabel(stepId, override) {
    if (override)
        return override;
    const desc = STEP_DESCRIPTORS[stepId];
    if (desc)
        return desc.defaultLabel;
    return stepId
        .replace(/^(step|plan)-/, '')
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
}
// ─────────────────────────────────────────────────────────────────────────────
// Helper functions
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Determine execution phase from lifecycle state and step data.
 */
export function deriveExecutionPhase(lifecycleState, steps) {
    // Terminal states
    if (lifecycleState === 'completed')
        return 'completed';
    if (lifecycleState === 'error')
        return 'failed';
    if (lifecycleState === 'cancelled')
        return 'cancelled';
    // Check active step types to determine phase
    const activeStep = steps.find((s) => s.status === 'running');
    if (activeStep) {
        switch (activeStep.type) {
            case 'retrieve_context':
                return 'retrieving_context';
            case 'classify_intent':
            case 'plan':
                return 'planning';
            case 'tool_execution':
                return 'executing_tools';
            case 'generate_response':
                return 'generating_response';
            default:
                break;
        }
    }
    // Check if we have any tool executions
    const hasToolExecution = steps.some((s) => s.type === 'tool_execution');
    if (hasToolExecution && lifecycleState === 'streaming') {
        return 'executing_tools';
    }
    // Streaming state
    if (lifecycleState === 'streaming') {
        return 'generating_response';
    }
    // Pending/initializing states
    if (lifecycleState === 'pending_start') {
        return 'queued';
    }
    // Default: initializing
    return 'initializing';
}
/**
 * Get a short summary label for the current execution state.
 *
 * Used in collapsed status display.
 */
export function getExecutionSummary(status) {
    const phaseLabel = ExecutionPhaseLabel(status.phase);
    // If actively executing a step, show its label
    if (status.activeStepId) {
        const activeStep = status.steps.find((s) => s.id === status.activeStepId);
        if (activeStep && activeStep.status === 'running') {
            return activeStep.label;
        }
    }
    // Otherwise show phase label
    return phaseLabel;
}
/**
 * Check if execution is in a terminal state.
 */
export function isExecutionTerminal(phase) {
    return phase === 'completed' || phase === 'failed' || phase === 'cancelled';
}
/**
 * Check if execution is actively running.
 */
export function isExecutionActive(phase) {
    return phase !== 'completed' && phase !== 'failed' && phase !== 'cancelled' && phase !== 'queued';
}
//# sourceMappingURL=ai-execution-status.js.map